/**
 * Project Settings → Inventory (G10): components, technologies, services, accounts, workflows,
 * agent skills and required tools, from each machine's sealed `inventory` entity. Every count
 * drills into its entries; every entry drills into its evidence (paths, sub-items, credentials as
 * `op://` references, readiness per machine with setup guidance). Values are never shown because
 * they are never stored.
 */
import { useQuery } from '@tanstack/react-query'
import { INVENTORY_CATEGORIES, type InventoryCategory, type InventoryStatus } from '@viko/protocol'
import { useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { CATEGORY_LABEL, type InventoryView, loadProjectInventory } from '../lib/inventory.js'
import type { Project } from '../lib/types.js'
import { Ago, Badge, Button, errorText, SkeletonRows, useToast } from './ui.js'

export function ProjectInventory({ project }: { project: Project }) {
  const b = useBackend()
  const entities = b.entities
  const q = useQuery({
    queryKey: ['inventory', project.id],
    queryFn: () => loadProjectInventory(entities as NonNullable<typeof entities>, project.id),
    enabled: !!entities,
    staleTime: 60_000,
  })
  return (
    <section className="grid gap-3" aria-labelledby="ps-inventory">
      <h3 id="ps-inventory" className="m-0 text-[14px] font-semibold">
        Inventory
      </h3>
      {!entities ? (
        <p className="m-0 text-[14px] text-text-3">
          The inventory is end-to-end encrypted; unlock this device to review it.
        </p>
      ) : q.isPending ? (
        <SkeletonRows n={3} />
      ) : q.isError ? (
        <p className="m-0 text-[14px] text-status-fail">{errorText(q.error)}</p>
      ) : (
        <InventoryBody view={q.data} />
      )}
    </section>
  )
}

export function InventoryBody({ view }: { view: InventoryView }) {
  const [cat, setCat] = useState<InventoryCategory | 'missing' | 'access' | undefined>(
    view.missing.length ? 'missing' : undefined,
  )
  if (!view.entries.length && !view.access.length)
    return (
      <p className="m-0 text-[14px] text-text-3">
        Nothing recorded yet. viko scans each checkout shortly after it starts, and agents add what
        they learn with <code>viko_inventory_update</code>.
      </p>
    )
  type Chip = [InventoryCategory | 'missing' | 'access', string, number]
  const chips: Chip[] = [
    ...(view.missing.length ? [['missing', 'Missing', view.missing.length] as Chip] : []),
    ...INVENTORY_CATEGORIES.map((c): Chip => [c, CATEGORY_LABEL[c], view.byCategory[c].length]),
    ['access', 'Secret uses', view.access.length],
  ]
  const shown =
    cat === 'missing' ? view.missing : cat && cat !== 'access' ? view.byCategory[cat] : []
  return (
    <div className="grid gap-3">
      <p className="m-0 text-[14px] text-text-3">
        {view.scannedAt ? (
          <>
            Scanned <Ago ts={view.scannedAt} />
          </>
        ) : (
          'Not scanned yet'
        )}
        {view.machines.length ? ` on ${view.machines.join(', ')}` : ''}
      </p>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Inventory categories">
        {chips.map(([id, label, n]) => (
          <Button
            key={id}
            size="sm"
            variant={cat === id ? 'primary' : 'secondary'}
            aria-pressed={cat === id}
            onClick={() => setCat(cat === id ? undefined : id)}
          >
            {label} <span className="tabular-nums">{n}</span>
          </Button>
        ))}
      </div>
      {cat === 'access' ? (
        <AccessList view={view} />
      ) : cat ? (
        <ul
          className="m-0 grid list-none gap-1 p-0"
          aria-label={cat === 'missing' ? 'Missing' : CATEGORY_LABEL[cat]}
        >
          {shown.map((e) => (
            <li key={e.key}>
              <details>
                <summary className="flex cursor-pointer flex-wrap items-center gap-2 py-1 text-[14px]">
                  <span className="font-medium text-text-1">{e.name}</span>
                  {e.version ? <span className="text-text-3">{e.version}</span> : null}
                  {e.required ? <Badge>required</Badge> : null}
                  <StatusBadge statuses={e.statuses} />
                  <span className="text-[14px] text-text-3">
                    {e.source === 'agent' ? 'agent' : e.source === 'user' ? 'you' : 'scan'}
                  </span>
                </summary>
                <EntryDetail entry={e} />
              </details>
            </li>
          ))}
          {!shown.length ? <li className="text-[14px] text-text-3">None</li> : null}
        </ul>
      ) : null}
    </div>
  )
}

function StatusBadge({ statuses }: { statuses: InventoryStatus[] }) {
  if (!statuses.length) return null
  const worst = statuses.some((s) => s.state === 'missing')
    ? 'missing'
    : statuses.some((s) => s.state === 'unverified')
      ? 'unverified'
      : 'ready'
  return (
    <Badge tone={worst === 'missing' ? 'fail' : worst === 'unverified' ? 'wait' : 'ok'}>
      {worst}
    </Badge>
  )
}

function EntryDetail({ entry: e }: { entry: InventoryView['entries'][number] }) {
  const toast = useToast()
  const copy = (text: string) =>
    void navigator.clipboard?.writeText(text).then(
      () => toast('Copied'),
      () => toast('Copy failed', 'error'),
    )
  return (
    <div className="grid gap-2 pb-3 pl-3 text-[14px]">
      {e.detail ? <p className="m-0 text-text-2">{e.detail}</p> : null}
      {e.url ? (
        <a href={e.url} target="_blank" rel="noopener noreferrer" className="break-all">
          {e.url}
        </a>
      ) : null}
      {e.statuses.map((s) => (
        <div key={`${s.machineId ?? ''}${s.checkedAt}`} className="grid gap-1">
          <span>
            <span className="font-medium">{s.machineId ?? 'machine'}:</span> {s.summary}{' '}
            <span className="text-text-3">
              (checked <Ago ts={s.checkedAt} />)
            </span>
          </span>
          {s.fix ? (
            <span className="flex flex-wrap items-center gap-2">
              <span>{s.fix.text}</span>
              {s.fix.command ? (
                <Button size="sm" onClick={() => copy(s.fix?.command ?? '')}>
                  Copy <code>{s.fix.command}</code>
                </Button>
              ) : null}
              {s.fix.url ? (
                <a href={s.fix.url} target="_blank" rel="noopener noreferrer">
                  Setup guide
                </a>
              ) : null}
            </span>
          ) : null}
        </div>
      ))}
      {e.credentials?.length ? (
        <ul className="m-0 grid list-none gap-1 p-0" aria-label="Credentials">
          {e.credentials.map((c) => (
            <li key={c.env} className="flex flex-wrap items-center gap-2">
              <code>{c.env}</code>
              <code className="break-all text-text-3">{c.ref}</code>
              <Badge>
                {c.vault === 'personal' ? 'personal vault · Touch ID' : 'project vault'}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {e.paths?.length ? (
        <ul className="m-0 grid list-none gap-0.5 p-0" aria-label="Found in">
          {e.paths.map((p) => (
            <li key={p}>
              <code className="break-all">{p}</code>
            </li>
          ))}
        </ul>
      ) : null}
      {e.items?.length ? (
        <details>
          <summary className="cursor-pointer text-text-2">
            {e.items.length} item{e.items.length === 1 ? '' : 's'}
          </summary>
          <ul className="m-0 mt-1 grid list-none gap-0.5 p-0">
            {e.items.map((i) => (
              <li key={i}>
                <code className="break-all">{i}</code>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {e.tags?.length ? <span className="text-text-3">{e.tags.join(' · ')}</span> : null}
      {e.runId ? <span className="text-text-3">Added by run {e.runId}</span> : null}
    </div>
  )
}

function AccessList({ view }: { view: InventoryView }) {
  if (!view.access.length)
    return <p className="m-0 text-[14px] text-text-3">No brokered secret uses yet.</p>
  return (
    <ul className="m-0 grid list-none gap-1 p-0" aria-label="Secret uses">
      {view.access.map((a) => (
        <li
          key={`${a.machineId ?? ''}${a.ts}${a.runId}${a.ref}`}
          className="flex flex-wrap items-center gap-2 text-[14px]"
        >
          <Badge
            tone={a.outcome === 'ok' ? 'ok' : a.outcome === 'pending_approval' ? 'wait' : 'fail'}
          >
            {a.outcome.replace('_', ' ')}
          </Badge>
          <code>{a.command}</code>
          <code className="break-all text-text-3">{a.ref}</code>
          <span className="text-text-3">
            {a.vault} vault{a.cached ? ' · batch unlock' : ''}
            {a.exitCode !== undefined ? ` · exit ${a.exitCode}` : ''} · run {a.runId}
            {a.machineId ? ` · ${a.machineId}` : ''} · <Ago ts={a.ts} />
          </span>
        </li>
      ))}
    </ul>
  )
}
